import Input from '5-react-form-apps/FormElements/Input';
import { Controller, useFormContext, type ControllerRenderProps } from 'react-hook-form';
import FormFieldError from '5-react-form-apps/FormComponents/FormFieldError/FormFieldError';
import type { TopUpFormData } from '../../TopUp';
import { type FC, useCallback } from 'react';

interface TopUpAmountProps {
    topUpLimit: number;
}

const TopUpAmount: FC<TopUpAmountProps> = ({ topUpLimit }) => {
    const { control, getValues, setValue: setFormValue, formState: { errors }, reset } = useFormContext<TopUpFormData>();

    const onAmountClick = useCallback((amount: number): void => {
        reset({ ...getValues(), otherAmount: undefined });
        setFormValue('amount', amount);
    }, [getValues, reset, setFormValue]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>, field: ControllerRenderProps<TopUpFormData, 'otherAmount'>): void => {
        const { value } = event.target;
        // Allow up to 2 decimal places only
        if ((/^\d*(?:\.\d{0,2})?$/).test(value)) {
            setFormValue('amount', undefined);
            const otherAmountValue = Number.parseFloat(value);
            if (isNaN(otherAmountValue)) {
                field.onChange(undefined);
                return;
            }
            field.onChange(otherAmountValue);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (event.key === 'e' || event.key === 'E' || event.key === '-' || event.key === '+') {
            event.preventDefault();
        }
    };

    return (
        <div className="top-up-amount-wrapper">
            <label>Amount</label>
            <Controller
                control={control}
                name="amount"
                render={({ field }): JSX.Element => {
                    return (
                        <div className="amount-group">
                            <button
                                type="button"
                                className={`amount-item ${field.value === 50 ? 'is-selected' : ''}`}
                                onClick={(): void => {
                                    onAmountClick(50);
                                }}
                            >
                                <p className="amount-label">$50</p>
                            </button>
                            <button
                                type="button"
                                className={`amount-item ${field.value === 100 ? 'is-selected' : ''}`}
                                onClick={(): void => {
                                    onAmountClick(100);
                                }}
                            >
                                <p className="amount-label">$100</p>
                            </button>
                            <button
                                type="button"
                                className={`amount-item ${field.value === 200 ? 'is-selected' : ''}`}
                                onClick={(): void => {
                                    onAmountClick(200);
                                }}
                            >
                                <p className="amount-label">$200</p>
                            </button>
                        </div>
                    );
                }}
            />

            <Controller
                control={control}
                name="otherAmount"
                rules={{
                    validate: (value): string | boolean => {
                        if (value && value > topUpLimit) {
                            return `$${topUpLimit} is the maximum you can top up`;
                        }

                        return true;
                    },
                }}
                render={({ field }): JSX.Element => {
                    return (
                        <Input
                            placeholder="Other amount"
                            prefixIcon="dollar"
                            fullWidth={true}
                            type="number"
                            className="top-up-amount-input"
                            hasError={!!errors.otherAmount}
                            onKeyDown={handleKeyDown}
                            value={field.value || ''}
                            onChange={(e): void => {
                                handleInputChange(e, field);
                            }}
                            // Prevent scroll wheel from changing value
                            onWheel={(e): void => {
                                e.currentTarget.blur();
                                e.preventDefault();
                            }}
                        />
                    );
                }}
            />
            {errors.otherAmount?.message && <FormFieldError message={errors.otherAmount.message} />}
        </div>
    );
};

export default TopUpAmount;
